/**
 * RepresentantesTab - Tab for managing cliente representatives
 */
import React, { useMemo } from 'react';
import { Plus, Upload, FileDown, User, Edit, Trash2, Info } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Group, Badge } from '@mantine/core';
import type { Representante } from '../../services/representantesService';
import { formatarCPF, formatarCNPJ, mascaraTelefone } from '../../lib/utils';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import PermissionGate from '../PermissionGate';

interface RepresentantesTabProps {
  representantes: Representante[];
  loading: boolean;
  onNewRepresentante: () => void;
  onEditRepresentante: (representante: Representante) => void;
  onDeleteRepresentante: (id: number) => void;
  onExportExcel: () => void;
  onOpenImportModal: () => void;
  onOpenFormatInfo: () => void;
}

// Helper function para formatar contagem de hospitais
const formatHospitalCount = (count: number | undefined): string => {
  if (count === undefined || count === 0) return '0 hospitais';
  return count === 1 ? '1 hospital' : `${count} hospitais`;
};

export const RepresentantesTab: React.FC<RepresentantesTabProps> = ({
  representantes,
  loading,
  onNewRepresentante,
  onEditRepresentante,
  onDeleteRepresentante,
  onExportExcel,
  onOpenImportModal,
  onOpenFormatInfo,
}) => {
  // Columns definition
  const columns = useMemo<MRT_ColumnDef<Representante>[]>(
    () => [
      { accessorKey: 'nome', header: 'Nome' },
      { accessorKey: 'email', header: 'Email' },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        accessorFn: (row) => row.telefone ? mascaraTelefone(row.telefone) : (row as any).celular ? mascaraTelefone((row as any).celular) : '-',
      },
      {
        accessorKey: 'cpf_cnpj',
        header: 'CPF/CNPJ',
        accessorFn: (row) => {
          const doc = row.cpf_cnpj?.replace(/\D/g, '') || '';
          if (doc.length === 11) return formatarCPF(doc);
          if (doc.length === 14) return formatarCNPJ(doc);
          return row.cpf_cnpj || '-';
        }
      },
      {
        id: 'hospitais_count',
        header: 'Hospitais',
        accessorFn: (row) => row.hospitais_count || 0,
        Cell: ({ cell }) => (
          <Badge color="blue" variant="light" size="sm">
            {formatHospitalCount(cell.getValue<number>())}
          </Badge>
        )
      }
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: representantes,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: { showColumnFilters: true, density: 'xs' },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: { 'th': { padding: '8px 12px !important' }, 'td': { padding: '4px 12px !important' } }
    },
    renderRowActions: ({ row }) => (
      <Group gap={4} wrap="nowrap">
        <PermissionGate tela="CLIENTES_LIST" acao="editar">
          <ActionIcon
            onClick={() => onEditRepresentante(row.original)}
            variant="subtle"
            color="gray"
            size="sm"
            title="Editar"
          >
            <Edit size={14} />
          </ActionIcon>
        </PermissionGate>
        <PermissionGate tela="CLIENTES_LIST" acao="excluir">
          <ActionIcon
            onClick={() => onDeleteRepresentante(row.original.id)}
            variant="subtle"
            color="red"
            size="sm"
            title="Deletar"
          >
            <Trash2 size={14} />
          </ActionIcon>
        </PermissionGate>
      </Group>
    ),
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" />
          Representantes do Cliente
        </h3>
      </div>

      {/* Action Toolbar */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-3 items-center justify-end">
          <div className="flex gap-2">
            <PermissionGate tela="CLIENTES_LIST" acao="exportar">
              <button
                onClick={onExportExcel}
                className="flex items-center gap-2 px-4 py-2 h-9 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <FileDown className="w-4 h-4" />
                Exportar Excel
              </button>
            </PermissionGate>
            <PermissionGate tela="CLIENTES_LIST" acao="editar">
              <button
                onClick={onOpenImportModal}
                className="flex items-center gap-2 px-4 py-2 h-9 border border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-sm"
              >
                <Upload className="w-4 h-4" />
                Importar Excel
              </button>
            </PermissionGate>
            <PermissionGate tela="CLIENTES_LIST" acao="editar">
              <button
                onClick={onOpenFormatInfo}
                className="flex items-center justify-center w-9 h-9 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Informações sobre o formato do Excel"
              >
                <Info className="w-4 h-4" />
              </button>
            </PermissionGate>
            <PermissionGate tela="CLIENTES_LIST" acao="editar">
              <button
                onClick={onNewRepresentante}
                className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Novo Representante
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando representantes...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && representantes.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhum representante encontrado
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Comece adicionando representantes ao cliente
          </p>
          <button
            onClick={onNewRepresentante}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Adicionar Primeiro Representante
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && representantes.length > 0 && (
        <MantineReactTable table={table} />
      )}
    </div>
  );
};
